/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AddEditCSRComponent } from './CSRs/addEditCSR/addEditCSR.component';
import { AddEditCertComponent } from './Certs/addEditCert/addEditCert.component';
import { BaseComponent } from '../../../../../core/components';
import { CertModel, CertsApiService } from '../../../shared';
import { ChangeDetectorRef, Component, ComponentRef, ViewChild, ViewContainerRef } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import {
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    NavigationService,
    NotifyService,
    RbacService,
    UtilsService
} from '../../../../../core/services';
import { Router } from '@angular/router';
import { UploadPemPfxComponent } from './pemPfx/uploadPemPfx/uploadPemPfx.component';
import { forkJoin, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-ssl-certificate',
    styleUrls: ['./ssl-certificate.component.scss'],
    templateUrl: './ssl-certificate.component.html'
})

export class SSLCertificateComponent extends BaseComponent {
    /**
     * Reference to embedded component container.
     */
    @ViewChild('addEditContainer', { read: ViewContainerRef, static: true })
    public addEditContainer: any;

    /**
     * Embedded in wizard flag.
     */
    public componentEmbedded = false;

    /**
     * Store the callback function (when embedded in wizard).
     */
    public callBack: (...args: any[]) => void;

    /**
     * An array of current certificates.
     */
    public certs: any[];

    /**
     * Visibility flags for embedded pages.
     */
    public isEmbeddedPageOpen = false;

    /**
     * Store the rows for each of the tables.
     */
    public certsRows: any[] = [];
    public csrsRows: any[] = [];
    public casRows: any[] = [];
    public crlsRows: any[] = [];

    /**
     * Store CSR data.
     */
    public csrData: any;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Store component reference.
     * @private
     */
    private componentReference: ComponentRef<any>;

    constructor(
        private cdr: ChangeDetectorRef,
        private certsService: CertsApiService,
        private languageService: LanguageService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private navigationService: NavigationService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private router: Router,
        private utilsService: UtilsService,
    ) {
        super();
        this.registerButtons();
        this.setUpPageData();
        this.setUpModalSubscribers();
    }

    /**
     * View csr in modal
     * @param id
     */
    public viewCSR(id: string): void {
        this.certsService.getCSR(id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (csr: CertModel): void => {
                    this.csrData = csr;
                    this.modalService.openModalInParent('view-csr-modal');
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('SSLCertificateComponent: viewCSR', 'failed to fetch CSR', error);
                }
            });
    }

    /**
     * Renew selected certificate.
     * @param id
     */
    public renewSelfSigned(id: string): void {
        this.certsService.renewSelfSigned(id)
            .pipe(
                takeUntil(this.destroyed$)
            )
            .subscribe({
                next: (): void => {
                    this.notifyService.clearLocal();
                    this.setUpPageData();
                }
            })
            .add((): void => {
                this.loadingService.loadingActionDotsEmitter.next(false);
            });
    }

    /**
     * Confirm delete cert.
     * @param row
     * @param type
     * @param apiCall
     */
    public confirmDelete(row: any, type: string, apiCall: string): void {
        const message: string = this.languageService.translate('confirm_delete_message');
        const confirmationObject = {
            title: 'confirm_delete',
            message: `${message}${type} - ${row.name}?`,
            callBack: this.deleteCert.bind(this, row.id, apiCall),
            options: { translateText: false }
        };
        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Open embedded page.
     * @param url
     * @param page
     * @param id
     */
    public openEmbeddedPage(url: string, page: string, id?: string): void {
        if (this.componentEmbedded) {
            this.toggleEmbeddedComponent(page, id);
        } else {
            this.router.navigateByUrl(this.navigationService.getEnduranceUrl(this.formatUrlString(url))).then().catch();
        }
    }

    /**
     * Format url string.
     * @param url
     * @private
     */
    private formatUrlString(url: string): string {
        return '/cluster-configuration/ssl-certificate' + url;
    }

    /**
     * Toggle embedded component.
     * @param page
     * @param id
     * @private
     */
    private toggleEmbeddedComponent(page: string, id?: string): void {
        if (!this.componentEmbedded) {
            return;
        }
        switch (page) {
            case 'addEditCert':
                this.setUpEmbeddedComponent(AddEditCertComponent, 'cert', id);
                break;
            case 'addEditCSR':
                this.setUpEmbeddedComponent(AddEditCSRComponent, 'csr', id);
                break;
            case 'addEditCA':
                this.setUpEmbeddedComponent(AddEditCertComponent, 'ca', id);
                break;
            case 'addEditCRL':
                this.setUpEmbeddedComponent(AddEditCertComponent, 'crl', id);
                break;
            case 'uploadCert':
                this.setUpEmbeddedComponent(UploadPemPfxComponent, id);
                break;
            default:
                break;
        }
    }

    /**
     * Create instance of toggle component.
     * @param component
     * @param page
     * @param id
     * @private
     */
    private setUpEmbeddedComponent(component: any, page: string, id?: string): void {
        this.isEmbeddedPageOpen = true;
        const embeddedComponent = this.addEditContainer.createComponent(component);
        embeddedComponent.instance.id = id;
        embeddedComponent.instance.embeddedPageType = page;
        embeddedComponent.instance.componentEmbedded = true;
        embeddedComponent.instance.callBack = this.cancelView.bind(this);

        this.componentReference = embeddedComponent;
        this.cdr.detectChanges();
    }

    /**
     * Cancel the embedded views.
     * @private
     */
    private cancelView(): void {
        this.isEmbeddedPageOpen = false;
        this.setUpPageData();
        this.componentReference.destroy();
    }

    /**
     * Delete a cert.
     * @param id
     * @param apiCall
     * @private
     */
    private deleteCert(id: string, apiCall: string): Observable<any> {
        return new Observable((observer) => {
            this.certsService[apiCall](id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.notifyService.clearLocal();
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(`SSLCertificateComponent: ${apiCall}`, 'delete failed', error);
                        observer.error(error);
                        observer.complete();
                    },
                    complete: (): void => {
                        this.loadingService.loadingActionDotsEmitter.next(false);
                    }
                });
        });
    }

    /**
     * Format cert date.
     * @param cert
     * @private
     */
    private formatDateForCert(cert: CertModel): string {
        const dateTime = new Date(cert.validTo);
        const locale: string = Intl.DateTimeFormat().resolvedOptions().locale;

        return dateTime.toLocaleDateString(locale);
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = this.setUpDataFetch();

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: any): void => {
                    if (dataGroup['certs']) {
                        this.certs = dataGroup['certs'];
                        this.populateCertsRows();
                    }

                    if (dataGroup['csrs']) {
                        this.populateCSRRows(dataGroup['csrs']);
                    }

                    if (dataGroup['cas']) {
                        this.populateCAsRows(dataGroup['cas']);
                    }

                    if (dataGroup['crls']) {
                        this.populateCRLsRows(dataGroup['crls']);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('SSLCertsComponent: setUpPageData ', 'failed to fetch data ', error);
                }
            });
    }

    /**
     * Set up api get requests.
     * @private
     */
    private setUpDataFetch(): any {
        const permissions = this.permissionsLookup;
        const apiCalls: any = {};
        apiCalls['certs'] = permissions['ssl-list-certs'] ? this.certsService.getCerts() : null;
        apiCalls['csrs'] = permissions['ssl-list-csrs'] ? this.certsService.getCSRs() : null;
        apiCalls['cas'] = permissions['ssl-list-cas'] ? this.certsService.getCAs() : null;
        apiCalls['crls'] = permissions['ssl-list-crls'] ? this.certsService.getCRLs() : null;

        return this.utilsService.cleanObject(apiCalls);
    }

    /**
     * Populate certs rows.
     * @private
     */
    private populateCertsRows(): void {
        this.certsRows = [];
        for (const cert of this.certs) {
            this.certsRows.push({
                id: cert.id,
                name: cert.name,
                subjectAlts: cert.certInfo.subjectAlts,
                commonName: cert.certInfo.commonName,
                validTo: this.formatDateForCert(cert),
                expiryWarning: this.getExpirationWarning(cert),
                daysToExpiry: this.getDaysToExpiry(cert),
                state: this.getWarningState(cert),
                usage: cert.usage
            });
        }
    }

    /**
     * Get expiration warning message.
     * @param cert
     * @private
     */
    private getExpirationWarning(cert: any): string {
        const daysUntilExpiry: number = this.getDaysToExpiry(cert);

        if (daysUntilExpiry > 0 && daysUntilExpiry <= 30) {
            return `${this.languageService.translate('certificate_expires_x_days', daysUntilExpiry)}`;
        } else if (daysUntilExpiry === 0) {
            return `${this.languageService.translate('certificate_expired_today')}`;
        } else if (daysUntilExpiry < 0) {
            return `${this.languageService.translate('certificate_expired_x_days_ago', Math.abs(daysUntilExpiry))}`;
        } else {
            return '';
        }
    }

    /**
     * Get number of days to expiry date.
     * @param cert
     * @private
     */
    private getDaysToExpiry(cert: any): number {
        const { validTo } = cert;
        const currentDate = Date.now();

        return this.utilsService.getTimeDifference(currentDate, validTo).days;
    }

    /**
     * Get warning state.
     * @param cert
     * @private
     */
    private getWarningState(cert: any): string {
        const daysToExpire: number = this.getDaysToExpiry(cert);

        if (daysToExpire <= 30 && daysToExpire >= 15) {
            return 'warning';
        } else if (daysToExpire < 15) {
            return 'alert';
        } else {
            return '';
        }
    }

    /**
     * Populate CSR rows.
     * @param csrs
     * @private
     */
    private populateCSRRows(csrs: CertModel[]): void {
        this.csrsRows = [];
        for (const csr of csrs) {
            this.csrsRows.push({
                id: csr.id,
                name: csr.name,
                commonName: csr.certInfo.commonName,
                organisation: csr.certInfo.organisation,
                subjectAlt: csr.certInfo.subjectAlt,
                selfSigned: csr.selfSigned,
                linkCertId: csr.linkCertId
            });
        }
    }

    /**
     * Populate CAs list.
     * @param cas
     * @private
     */
    private populateCAsRows(cas: any[]): void {
        this.casRows = [];
        for (const ca of cas) {
            this.casRows.push({
                id: ca.id,
                name: ca.name,
                commonName: ca.certInfo.commonName,
                validTo: this.formatDateForCert(ca),
                usage: ca.usage
            });
        }
    }

    /**
     * Populate CRLs list.
     * @param crls
     * @private
     */
    private populateCRLsRows(crls: any[]): void {
        this.crlsRows = [];
        for (const crl of crls) {
            this.crlsRows.push({
                id: crl.id,
                name: crl.name,
                usage: crl.usage
            });
        }
    }

    /**
     * Set up modal subscribers.
     * @private
     */
    private setUpModalSubscribers(): void {
        this.modalService.closeParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (modalId: string): void => {
                    if (modalId === 'view-csr-modal') {
                        this.loadingService.loadingActionDotsEmitter.next(false);
                    }
                }
            });
    }

    /**
     * Register button permissions.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'ssl-certificates-create-cert': this.getCreateCertPaths(),
            'ssl-certificates-upload': [{ path: '/certificates/certs', methods: ['POST'] }],
            'ssl-certificates-edit-cert': [{ path: '/certificates/certs/:id', methods: ['PUT'] }],
            'ssl-certificates-delete-cert': [{ path: '/certificates/certs/:id', methods: ['DELETE'] }],
            'ssl-certificates-create-csr': [{ path: '/certificates/csrs', methods: ['POST'] }],
            'ssl-certificates-delete-csr': [{ path: '/certificates/csrs/:id', methods: ['DELETE'] }],
            'ssl-certificates-apply-csr': [{ path: 'certificates/csrs/:id/apply', methods: ['PUT'] }],
            'ssl-certificates-edit-csr': [{ path: '/certificates/csrs/:id', methods: ['PUT'] }],
            'ssl-certificates-view-csr': [{ path: '/certificates/csrs/:id', methods: ['GET'] }],
            'ssl-certificates-create-ca': [{ path: '/certificates/cas', methods: ['POST'] }],
            'ssl-certificates-edit-ca': [{ path: '/certificates/cas/:id', methods: ['PUT'] }],
            'ssl-certificates-delete-ca': [{ path: '/certificates/cas/:id', methods: ['GET'] }],
            'ssl-certificates-create-crl': [{ path: '/certificates/crls', methods: ['POST'] }],
            'ssl-certificates-edit-crl': [{ path: '/certificates/crls/:id', methods: ['PUT'] }],
            'ssl-certificates-delete-crl': [{ path: '/certificates/crls/:id', methods: ['GET'] }],
            'ssl-list-certs': [{ path: '/certificates/certs', methods: ['GET'] }],
            'ssl-list-csrs': [{ path: '/certificates/csrs', methods: ['GET'] }],
            'ssl-list-cas': [{ path: '/certificates/cas', methods: ['GET'] }],
            'ssl-list-crls': [{ path: '/certificates/crls', methods: ['GET'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }

    /**
     * Retrieve paths for creating a certificate
     * @private
     */
    private getCreateCertPaths(): any {
        return [
            { path: '/certificates/certs', methods: ['POST'] },
            { path: '/certificates/csrs', methods: ['POST'] }
        ];
    }
}
